import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';
import {
  Bot,
  BriefcaseBusiness,
  CheckCircle2,
  ExternalLink,
  GraduationCap,
  Layers3,
  MessageSquareText,
  Search,
  Trash2,
  UserRoundPlus,
  UsersRound,
  Wand2,
  X,
} from 'lucide-react';
import { api } from '../services/api';
import type {
  CreateDemoUserPayload,
  DemoSourceChat,
  DemoSourceTwin,
  DemoSourceTwinChat,
  DemoSourceUser,
  DemoUserSummary,
  User,
} from '../types';
import { Layout } from '../components/layout/Layout';
import { Card, CardHeader } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Modal } from '../components/ui/Modal';
import { useToast } from '../components/ui/Toast';
import { cn, formatDate } from '../utils/helpers';

const DEFAULT_PASSWORD = 'Demo@12345';

type SourceFilter = 'all' | 'company' | 'job_seeker';

type DemoFormState = {
  name: string;
  email: string;
  password: string;
  user_type: 'company' | 'job_seeker';
  date_of_birth: string;
  gender: string;
  address: string;
  state: string;
  city: string;
  country: string;
  education_level: string;
  occupation: string;
  is_student: boolean;
  bio: string;
  profile_picture: string;
  credits: string;
};

const emptyForm: DemoFormState = {
  name: '',
  email: '',
  password: DEFAULT_PASSWORD,
  user_type: 'company',
  date_of_birth: '',
  gender: '',
  address: '',
  state: '',
  city: '',
  country: '',
  education_level: '',
  occupation: '',
  is_student: false,
  bio: '',
  profile_picture: '',
  credits: '0',
};

function buildDemoEmail(source: DemoSourceUser['sourceUser']) {
  const safeName = source.name.toLowerCase().replace(/[^a-z0-9]+/g, '.').replace(/^\.+|\.+$/g, '');
  return `demo+${safeName || source.id}@obaet.demo`;
}

function userTypeLabel(userType: User['userType']) {
  return userType === 'company' ? 'Recruiter' : userType === 'job_seeker' ? 'Job Seeker' : 'Unknown';
}

function dedupeById<T extends { id: string }>(items: T[]) {
  const seen = new Set<string>();
  return items.filter((item) => {
    if (!item.id || seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
}

function SelectionCard({
  title,
  subtitle,
  selected,
  onToggle,
  meta,
  badge,
}: {
  title: string;
  subtitle: string;
  selected: boolean;
  onToggle: () => void;
  meta: string;
  badge?: string;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={cn(
        'w-full rounded-2xl border p-4 text-left transition-all duration-200',
        selected
          ? 'border-[#3b82f6] bg-[#111a2d] shadow-[0_0_0_1px_rgba(59,130,246,0.25)]'
          : 'border-[#252535] bg-[#10101a] hover:border-[#364152] hover:bg-[#151523]'
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <p className="truncate text-sm font-semibold text-[#f8fafc]">{title}</p>
            {badge && (
              <span className="rounded-full bg-[#1a2235] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#8fb4ff]">
                {badge}
              </span>
            )}
          </div>
          <p className="mt-1 text-xs leading-5 text-[#94a3b8]">{subtitle}</p>
        </div>
        <div
          className={cn(
            'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border',
            selected ? 'border-[#3b82f6] bg-[#3b82f6] text-white' : 'border-[#3a3a4a] bg-transparent'
          )}
        >
          {selected && <CheckCircle2 className="h-3.5 w-3.5" />}
        </div>
      </div>
      <p className="mt-3 text-[11px] font-medium uppercase tracking-[0.18em] text-[#64748b]">{meta}</p>
    </button>
  );
}

export function DemoUsers() {
  const { toast } = useToast();
  const [demoUsers, setDemoUsers] = useState<DemoUserSummary[]>([]);
  const [sourceSearch, setSourceSearch] = useState('');
  const [sourceTypeFilter, setSourceTypeFilter] = useState<SourceFilter>('all');
  const [sourceResults, setSourceResults] = useState<User[]>([]);
  const [searchingSources, setSearchingSources] = useState(false);
  const [selectedSources, setSelectedSources] = useState<DemoSourceUser[]>([]);
  const [loadingSourceIds, setLoadingSourceIds] = useState<string[]>([]);
  const [profileSourceId, setProfileSourceId] = useState<string | null>(null);
  const [form, setForm] = useState<DemoFormState>(emptyForm);
  const [selectedTwinIds, setSelectedTwinIds] = useState<string[]>([]);
  const [selectedTwinChatIds, setSelectedTwinChatIds] = useState<string[]>([]);
  const [selectedChatIds, setSelectedChatIds] = useState<string[]>([]);
  const [creating, setCreating] = useState(false);
  const [loadingDemoUsers, setLoadingDemoUsers] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState<DemoUserSummary | null>(null);
  const [deleting, setDeleting] = useState(false);

  const mergedTwins = dedupeById(selectedSources.flatMap((source) => source.twins));
  const mergedTwinChats = dedupeById(selectedSources.flatMap((source) => source.twinChats));
  const mergedChats = dedupeById(selectedSources.flatMap((source) => source.chats));
  const selectedSourceIds = selectedSources.map((source) => source.sourceUser.id);
  const profileSource = selectedSources.find((source) => source.sourceUser.id === profileSourceId) || null;

  const fetchDemoUsers = useCallback(async () => {
    try {
      setLoadingDemoUsers(true);
      const response = await api.getDemoUsers();
      setDemoUsers(response.users);
    } catch (error) {
      console.error(error);
      toast(error instanceof Error ? error.message : 'Failed to load demo users', 'error');
    } finally {
      setLoadingDemoUsers(false);
    }
  }, [toast]);

  const searchUsers = useCallback(
    async (term: string, filter: SourceFilter) => {
      try {
        setSearchingSources(true);
        const response = await api.getUsers({
          search: term || undefined,
          userType: filter === 'all' ? undefined : filter,
          page: 1,
          limit: 16,
          sortBy: '-createdAt',
        });
        setSourceResults(response.users.filter((user) => !user.disabled));
    } catch (error) {
      console.error(error);
      toast(error instanceof Error ? error.message : 'Failed to search source users', 'error');
    } finally {
      setSearchingSources(false);
    }
  },
    [toast]
  );

  useEffect(() => {
    fetchDemoUsers();
  }, [fetchDemoUsers]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      searchUsers(sourceSearch, sourceTypeFilter);
    }, 250);
    return () => window.clearTimeout(handle);
  }, [searchUsers, sourceSearch, sourceTypeFilter]);

  useEffect(() => {
    const validTwinIds = new Set(mergedTwins.map((twin) => twin.id));
    setSelectedTwinIds((current) => {
      const next = current.filter((id) => validTwinIds.has(id));
      return next.length === current.length ? current : next;
    });
  }, [selectedSources, mergedTwins]);

  useEffect(() => {
    const validTwinIds = new Set(selectedTwinIds);
    const validTwinChatIds = new Set(
      mergedTwinChats.filter((chat) => validTwinIds.has(chat.sourceTwinId)).map((chat) => chat.id)
    );
    const validChatIds = new Set(mergedChats.filter((chat) => validTwinIds.has(chat.sourceTwinId)).map((chat) => chat.id));

    setSelectedTwinChatIds((current) => {
      const next = current.filter((id) => validTwinChatIds.has(id));
      return next.length === current.length ? current : next;
    });
    setSelectedChatIds((current) => {
      const next = current.filter((id) => validChatIds.has(id));
      return next.length === current.length ? current : next;
    });
  }, [mergedChats, mergedTwinChats, selectedTwinIds]);

  const setFormField = <K extends keyof DemoFormState>(key: K, value: DemoFormState[K]) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const applySourceToForm = useCallback((source: DemoSourceUser) => {
    setForm({
      name: source.sourceUser.name,
      email: buildDemoEmail(source.sourceUser),
      password: DEFAULT_PASSWORD,
      user_type: source.sourceUser.userType === 'job_seeker' ? 'job_seeker' : 'company',
      date_of_birth: source.sourceUser.profile.dateOfBirth || '',
      gender: source.sourceUser.profile.gender || '',
      address: source.sourceUser.profile.address || '',
      state: source.sourceUser.profile.state || '',
      city: source.sourceUser.profile.city || '',
      country: source.sourceUser.profile.country || '',
      education_level: source.sourceUser.profile.educationLevel || '',
      occupation: source.sourceUser.profile.occupation || '',
      is_student: source.sourceUser.profile.isStudent || false,
      bio: source.sourceUser.profile.bio || '',
      profile_picture: source.sourceUser.profile.profilePicture || '',
      credits: String(source.sourceUser.profile.credits ?? 0),
    });
  }, []);

  const handleToggleSource = async (user: User) => {
    const existing = selectedSources.find((source) => source.sourceUser.id === user.id);
    if (existing) {
      const remainingSources = selectedSources.filter((source) => source.sourceUser.id !== user.id);
      setSelectedSources(remainingSources);
      if (remainingSources.length === 0) {
        setProfileSourceId(null);
        setForm(emptyForm);
        setSelectedTwinIds([]);
        setSelectedTwinChatIds([]);
        setSelectedChatIds([]);
        return;
      }

      if (profileSourceId === user.id) {
        setProfileSourceId(remainingSources[0].sourceUser.id);
        applySourceToForm(remainingSources[0]);
      }
      return;
    }

    try {
      setLoadingSourceIds((current) => (current.includes(user.id) ? current : [...current, user.id]));
      const response = await api.getDemoSourceUser(user.id);
      setSelectedSources((current) => [...current, response]);
      setSelectedTwinIds((current) => [...new Set([...current, ...response.twins.map((twin) => twin.id)])]);
      setSelectedTwinChatIds((current) => [...new Set([...current, ...response.twinChats.map((chat) => chat.id)])]);
      setSelectedChatIds((current) => [...new Set([...current, ...response.chats.map((chat) => chat.id)])]);

      if (!profileSourceId && selectedSources.length === 0) {
        setProfileSourceId(response.sourceUser.id);
        applySourceToForm(response);
      }
    } catch (error) {
      console.error(error);
      toast(error instanceof Error ? error.message : 'Failed to load source user data', 'error');
    } finally {
      setLoadingSourceIds((current) => current.filter((id) => id !== user.id));
    }
  };

  const toggleSelection = (value: string, selected: string[], setSelected: (values: string[]) => void) => {
    setSelected(selected.includes(value) ? selected.filter((item) => item !== value) : [...selected, value]);
  };

  const handleToggleTwin = (twinId: string) => {
    const nextSelectedTwins = selectedTwinIds.includes(twinId)
      ? selectedTwinIds.filter((item) => item !== twinId)
      : [...selectedTwinIds, twinId];
    setSelectedTwinIds(nextSelectedTwins);

    setSelectedTwinChatIds((current) =>
      current.filter((chatId) => {
        const chat = mergedTwinChats.find((item) => item.id === chatId);
        return chat ? nextSelectedTwins.includes(chat.sourceTwinId) : true;
      })
    );
    setSelectedChatIds((current) =>
      current.filter((chatId) => {
        const chat = mergedChats.find((item) => item.id === chatId);
        return chat ? nextSelectedTwins.includes(chat.sourceTwinId) : true;
      })
    );
  };

  const handleCreateDemoUser = async () => {
    if (selectedSources.length === 0) {
      toast('Choose at least one source user first', 'error');
      return;
    }

    if (!form.name.trim() || !form.email.trim() || !form.password.trim()) {
      toast('Name, email, and password are required', 'error');
      return;
    }

    const validTwinIds = new Set(mergedTwins.map((twin) => twin.id));
    const submitTwinIds = selectedTwinIds.filter((id) => validTwinIds.has(id));
    const validTwinChatIds = new Set(
      mergedTwinChats.filter((chat) => submitTwinIds.includes(chat.sourceTwinId)).map((chat) => chat.id)
    );
    const validChatIds = new Set(
      mergedChats.filter((chat) => submitTwinIds.includes(chat.sourceTwinId)).map((chat) => chat.id)
    );
    const submitTwinChatIds = selectedTwinChatIds.filter((id) => validTwinChatIds.has(id));
    const submitChatIds = selectedChatIds.filter((id) => validChatIds.has(id));

    if (
      submitTwinIds.length !== selectedTwinIds.length ||
      submitTwinChatIds.length !== selectedTwinChatIds.length ||
      submitChatIds.length !== selectedChatIds.length
    ) {
      setSelectedTwinIds(submitTwinIds);
      setSelectedTwinChatIds(submitTwinChatIds);
      setSelectedChatIds(submitChatIds);
      toast('Your source selection changed, so I refreshed the twins and chats before creating the demo user.', 'error');
      return;
    }

    if (submitTwinIds.length === 0) {
      toast('Select at least one twin', 'error');
      return;
    }

    const payload: CreateDemoUserPayload = {
      source_user_id: profileSourceId || selectedSourceIds[0],
      source_user_ids: selectedSourceIds,
      primary_source_user_id: profileSourceId || selectedSourceIds[0],
      name: form.name.trim(),
      email: form.email.trim(),
      password: form.password,
      user_type: form.user_type,
      date_of_birth: form.date_of_birth || undefined,
      gender: form.gender || undefined,
      address: form.address || undefined,
      state: form.state || undefined,
      city: form.city || undefined,
      country: form.country || undefined,
      education_level: form.education_level || undefined,
      occupation: form.occupation || undefined,
      is_student: form.is_student,
      bio: form.bio || undefined,
      profile_picture: form.profile_picture || undefined,
      credits: Number(form.credits) || 0,
      selected_twin_ids: submitTwinIds,
      selected_twin_chat_ids: submitTwinChatIds,
      selected_chat_ids: submitChatIds,
    };

    try {
      setCreating(true);
      const response = await api.createDemoUser(payload);
      toast(
        `Demo user created with ${response.createdTwinCount} twins, ${response.createdTwinChatCount} twin chats, and ${response.createdChatCount} chats`,
        'success'
      );
      fetchDemoUsers();
    } catch (error) {
      console.error(error);
      toast(error instanceof Error ? error.message : 'Failed to create demo user', 'error');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteDemoUser = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await api.deleteDemoUser(deleteTarget.id);
      toast(`Deleted demo user ${deleteTarget.name}`, 'success');
      setDeleteTarget(null);
      fetchDemoUsers();
    } catch (error) {
      console.error(error);
      toast(error instanceof Error ? error.message : 'Failed to delete demo user', 'error');
    } finally {
      setDeleting(false);
    }
  };

  const twinChatsForSelectedTwins = mergedTwinChats.filter((chat) => selectedTwinIds.includes(chat.sourceTwinId));
  const chatsForSelectedTwins = mergedChats.filter((chat) => selectedTwinIds.includes(chat.sourceTwinId));

  return (
    <Layout>
      <div className="space-y-6">
        <div className="rounded-[28px] border border-[#2d3550] bg-[radial-gradient(circle_at_top_left,_rgba(59,130,246,0.18),_transparent_35%),linear-gradient(135deg,#121523_0%,#0b0d14_100%)] p-8 shadow-[0_24px_80px_rgba(8,15,35,0.45)]">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-2xl">
              <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-[#2b3c68] bg-[#0d1528]/80 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.22em] text-[#8fb4ff]">
                <Wand2 className="h-3.5 w-3.5" />
                Demo User Workshop
              </div>
              <h2 className="text-3xl font-semibold tracking-tight text-[#f8fafc]">
                Build one polished demo user from multiple real account histories
              </h2>
              <p className="mt-3 max-w-2xl text-sm leading-6 text-[#94a3b8]">
                Filter source users by recruiter or job seeker, select several accounts, then pull exactly the twins
                and conversations you want into one demo profile that can also be fully deleted later.
              </p>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Card className="min-w-[140px] border-[#24324f] bg-[#0d1322]/80" padding="sm">
                <p className="text-[11px] uppercase tracking-[0.18em] text-[#64748b]">Demo Users</p>
                <p className="mt-2 text-2xl font-semibold text-[#f8fafc]">{demoUsers.length}</p>
              </Card>
              <Card className="min-w-[140px] border-[#24324f] bg-[#0d1322]/80" padding="sm">
                <p className="text-[11px] uppercase tracking-[0.18em] text-[#64748b]">Source Users</p>
                <p className="mt-2 text-2xl font-semibold text-[#f8fafc]">{selectedSources.length}</p>
              </Card>
              <Card className="min-w-[140px] border-[#24324f] bg-[#0d1322]/80" padding="sm">
                <p className="text-[11px] uppercase tracking-[0.18em] text-[#64748b]">Selected Chats</p>
                <p className="mt-2 text-2xl font-semibold text-[#f8fafc]">{selectedTwinChatIds.length + selectedChatIds.length}</p>
              </Card>
            </div>
          </div>
        </div>

        <div className="grid gap-6 xl:grid-cols-[1.1fr_1.4fr]">
          <div className="space-y-6">
            <Card className="overflow-hidden border-[#29314a] bg-[#10141f]" padding="none">
              <div className="border-b border-[#232a3d] px-6 py-5">
                <div className="flex items-center gap-3">
                  <div className="rounded-2xl bg-[#17213a] p-3 text-[#8fb4ff]">
                    <Search className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-[#f8fafc]">1. Pick source users</h3>
                    <p className="text-sm text-[#94a3b8]">
                      Search by name or email, filter by recruiter or job seeker, and select multiple accounts.
                    </p>
                  </div>
                </div>
              </div>
              <div className="space-y-4 p-6">
                <Input
                  label="Search users"
                  placeholder="Search by name or email"
                  value={sourceSearch}
                  onChange={(event) => setSourceSearch(event.target.value)}
                />

                <div className="flex flex-wrap gap-2">
                  {[
                    { value: 'all' as const, label: 'All Users', icon: UsersRound },
                    { value: 'company' as const, label: 'Recruiters', icon: BriefcaseBusiness },
                    { value: 'job_seeker' as const, label: 'Job Seekers', icon: GraduationCap },
                  ].map((filter) => (
                    <button
                      key={filter.value}
                      type="button"
                      onClick={() => setSourceTypeFilter(filter.value)}
                      className={cn(
                        'inline-flex items-center gap-2 rounded-full border px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] transition-all duration-200',
                        sourceTypeFilter === filter.value
                          ? 'border-[#3b82f6] bg-[#172442] text-[#dbeafe]'
                          : 'border-[#26304a] bg-[#0d111b] text-[#94a3b8] hover:border-[#3a4b6d] hover:text-[#f8fafc]'
                      )}
                    >
                      <filter.icon className="h-3.5 w-3.5" />
                      {filter.label}
                    </button>
                  ))}
                </div>

                <div className="grid gap-3">
                  {searchingSources ? (
                    <div className="rounded-2xl border border-[#252535] bg-[#0f1018] px-4 py-8 text-center text-sm text-[#94a3b8]">
                      Searching users...
                    </div>
                  ) : (
                    sourceResults.map((user) => {
                      const isSelected = selectedSourceIds.includes(user.id);
                      const isLoading = loadingSourceIds.includes(user.id);
                      return (
                        <button
                          key={user.id}
                          type="button"
                          onClick={() => handleToggleSource(user)}
                          className={cn(
                            'rounded-2xl border px-4 py-3 text-left transition-all duration-200',
                            isSelected
                              ? 'border-[#3b82f6] bg-[#111a2d]'
                              : 'border-[#252535] bg-[#0f1018] hover:border-[#334155] hover:bg-[#151827]'
                          )}
                        >
                          <div className="flex items-center justify-between gap-3">
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="truncate text-sm font-semibold text-[#f8fafc]">{user.name}</p>
                                {isSelected && (
                                  <span className="rounded-full bg-[#17335f] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#bfdbfe]">
                                    Selected
                                  </span>
                                )}
                              </div>
                              <p className="truncate text-xs text-[#94a3b8]">{user.email}</p>
                            </div>
                            <span className="rounded-full bg-[#1a2235] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#8fb4ff]">
                              {isLoading ? 'Loading' : userTypeLabel(user.userType)}
                            </span>
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>

                {selectedSources.length > 0 && (
                  <div className="rounded-2xl border border-[#23304a] bg-[#0d1320] p-4">
                    <div className="mb-3 flex items-center justify-between gap-3">
                      <div>
                        <p className="text-[11px] uppercase tracking-[0.18em] text-[#64748b]">Selected sources</p>
                        <p className="mt-1 text-sm text-[#94a3b8]">
                          Choose which selected account should prefill the demo profile form.
                        </p>
                      </div>
                    </div>
                    <div className="space-y-3">
                      {selectedSources.map((source) => {
                        const isProfileSource = profileSourceId === source.sourceUser.id;
                        return (
                          <div
                            key={source.sourceUser.id}
                            className="flex flex-col gap-3 rounded-2xl border border-[#27324b] bg-[#0f1728] p-4"
                          >
                            <div className="flex flex-wrap items-start justify-between gap-3">
                              <div className="min-w-0">
                                <div className="flex flex-wrap items-center gap-2">
                                  <p className="truncate text-sm font-semibold text-[#f8fafc]">{source.sourceUser.name}</p>
                                  {isProfileSource && (
                                    <span className="rounded-full bg-[#18375f] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#bfdbfe]">
                                      Profile Source
                                    </span>
                                  )}
                                </div>
                                <p className="mt-1 truncate text-xs text-[#94a3b8]">{source.sourceUser.email}</p>
                                <p className="mt-2 text-xs text-[#64748b]">
                                  {source.twins.length} twins • {source.twinChats.length} twin chats • {source.chats.length} chats
                                </p>
                              </div>
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setProfileSourceId(source.sourceUser.id);
                                    applySourceToForm(source);
                                  }}
                                  className={cn(
                                    'rounded-full border px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] transition-all duration-200',
                                    isProfileSource
                                      ? 'border-[#3b82f6] bg-[#17335f] text-[#dbeafe]'
                                      : 'border-[#334155] bg-transparent text-[#94a3b8] hover:text-[#f8fafc]'
                                  )}
                                >
                                  Use For Profile
                                </button>
                                <button
                                  type="button"
                                  onClick={() =>
                                    handleToggleSource({
                                      id: source.sourceUser.id,
                                      name: source.sourceUser.name,
                                      email: source.sourceUser.email,
                                      userType: source.sourceUser.userType,
                                      disabled: false,
                                      createdAt: '',
                                      twinCount: source.twins.length,
                                    })
                                  }
                                  className="rounded-full border border-[#3b2330] bg-[#2a1119] p-2 text-[#fca5a5] transition-all duration-200 hover:border-[#7f1d1d] hover:text-[#fecaca]"
                                >
                                  <X className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </Card>

            <Card className="border-[#29314a] bg-[#10141f]">
              <CardHeader
                title="2. Shape the demo profile"
                subtitle={
                  profileSource
                    ? `The form is currently prefilled from ${profileSource.sourceUser.name}, and you can still edit everything.`
                    : 'Select at least one source user to prefill the form, then edit anything you want.'
                }
              />
              <div className="grid gap-4 md:grid-cols-2">
                <Input label="Name" value={form.name} onChange={(e) => setFormField('name', e.target.value)} />
                <Input label="Email" type="email" value={form.email} onChange={(e) => setFormField('email', e.target.value)} />
                <Input label="Password" value={form.password} onChange={(e) => setFormField('password', e.target.value)} />
                <div>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-[#94a3b8]">User type</label>
                  <select
                    value={form.user_type}
                    onChange={(e) => setFormField('user_type', e.target.value as DemoFormState['user_type'])}
                    className="w-full rounded-xl border border-[#252535] bg-[#0f0f1a] px-3 py-2.5 text-sm text-[#f1f5f9] transition-all duration-200"
                  >
                    <option value="company">Recruiter</option>
                    <option value="job_seeker">Job Seeker</option>
                  </select>
                </div>
                <Input label="Date of birth" value={form.date_of_birth} onChange={(e) => setFormField('date_of_birth', e.target.value)} />
                <Input label="Gender" value={form.gender} onChange={(e) => setFormField('gender', e.target.value)} />
                <Input label="Occupation" value={form.occupation} onChange={(e) => setFormField('occupation', e.target.value)} />
                <Input label="Education level" value={form.education_level} onChange={(e) => setFormField('education_level', e.target.value)} />
                <Input label="Address" value={form.address} onChange={(e) => setFormField('address', e.target.value)} className="md:col-span-2" />
                <Input label="State" value={form.state} onChange={(e) => setFormField('state', e.target.value)} />
                <Input label="City" value={form.city} onChange={(e) => setFormField('city', e.target.value)} />
                <Input label="Country" value={form.country} onChange={(e) => setFormField('country', e.target.value)} />
                <Input label="Credits" type="number" value={form.credits} onChange={(e) => setFormField('credits', e.target.value)} />
                <Input
                  label="Profile picture URL"
                  value={form.profile_picture}
                  onChange={(e) => setFormField('profile_picture', e.target.value)}
                  className="md:col-span-2"
                />
                <label className="flex items-center gap-3 rounded-xl border border-[#252535] bg-[#0f0f1a] px-4 py-3 text-sm text-[#f8fafc] md:col-span-2">
                  <input
                    type="checkbox"
                    checked={form.is_student}
                    onChange={(e) => setFormField('is_student', e.target.checked)}
                    className="h-4 w-4 rounded border-[#3a3a4a] bg-[#0b0d14]"
                  />
                  Mark this demo user as a student
                </label>
                <div className="md:col-span-2">
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-[#94a3b8]">Bio</label>
                  <textarea
                    value={form.bio}
                    onChange={(e) => setFormField('bio', e.target.value)}
                    rows={4}
                    className="w-full rounded-xl border border-[#252535] bg-[#0f0f1a] px-3 py-2.5 text-sm text-[#f1f5f9] placeholder-[#64748b]"
                    placeholder="Describe the user in a way that feels ready for a live demo."
                  />
                </div>
              </div>
            </Card>

            <div className="flex items-center justify-end">
              <Button
                variant="primary"
                size="lg"
                loading={creating}
                onClick={handleCreateDemoUser}
                icon={<UserRoundPlus className="h-4 w-4" />}
              >
                Create Demo User
              </Button>
            </div>
          </div>

          <div className="space-y-6">
            <Card className="border-[#29314a] bg-[#10141f]">
              <CardHeader
                title="3. Curate imported twins and conversations"
                subtitle="Only the twins, twin chats, and human chats from the selected source users appear here."
              />
              {selectedSources.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-[#2d3650] bg-[#0d111b] px-6 py-16 text-center">
                  <Layers3 className="mx-auto h-8 w-8 text-[#64748b]" />
                  <p className="mt-4 text-base font-medium text-[#f8fafc]">No source users selected yet</p>
                  <p className="mt-2 text-sm text-[#94a3b8]">Pick one or more source users on the left to load their twins and chats.</p>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="rounded-2xl border border-[#23304a] bg-[#0d1320] p-5">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <p className="text-[11px] uppercase tracking-[0.18em] text-[#64748b]">Merged inventory</p>
                        <h3 className="mt-2 text-xl font-semibold text-[#f8fafc]">{selectedSources.length} selected source users</h3>
                        <p className="mt-1 text-sm text-[#94a3b8]">Everything below comes only from the accounts you selected above.</p>
                      </div>
                      <div className="rounded-2xl border border-[#26324b] bg-[#0f1728] px-4 py-3">
                        <p className="text-[11px] uppercase tracking-[0.18em] text-[#64748b]">Inventory</p>
                        <p className="mt-2 text-sm text-[#f8fafc]">
                          {mergedTwins.length} twins • {mergedTwinChats.length} twin chats • {mergedChats.length} chats
                        </p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <div className="mb-3 flex items-center justify-between">
                      <h4 className="text-sm font-semibold uppercase tracking-[0.18em] text-[#94a3b8]">Twins</h4>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          setSelectedTwinIds(selectedTwinIds.length === mergedTwins.length ? [] : mergedTwins.map((twin) => twin.id))
                        }
                      >
                        {selectedTwinIds.length === mergedTwins.length ? 'Clear' : 'Select all'}
                      </Button>
                    </div>
                    <div className="grid gap-3 md:grid-cols-2">
                      {mergedTwins.map((twin: DemoSourceTwin) => (
                        <SelectionCard
                          key={twin.id}
                          title={twin.name}
                          subtitle={`${twin.role || 'Unknown role'} -> ${twin.target || 'Unknown target'}`}
                          meta={`${twin.sourceUserName} • ${twin.quiz?.length || 0} quiz items • understanding ${twin.understanding}`}
                          badge={userTypeLabel(twin.type)}
                          selected={selectedTwinIds.includes(twin.id)}
                          onToggle={() => handleToggleTwin(twin.id)}
                        />
                      ))}
                    </div>
                  </div>

                  <div>
                    <div className="mb-3 flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <Bot className="h-4 w-4 text-[#8fb4ff]" />
                        <h4 className="text-sm font-semibold uppercase tracking-[0.18em] text-[#94a3b8]">Twin Chats</h4>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          setSelectedTwinChatIds(
                            selectedTwinChatIds.length === twinChatsForSelectedTwins.length ? [] : twinChatsForSelectedTwins.map((chat) => chat.id)
                          )
                        }
                      >
                        {selectedTwinChatIds.length === twinChatsForSelectedTwins.length ? 'Clear' : 'Select all'}
                      </Button>
                    </div>
                    <div className="grid gap-3">
                      {twinChatsForSelectedTwins.length === 0 ? (
                        <div className="rounded-2xl border border-dashed border-[#2c3348] bg-[#0e1119] px-4 py-8 text-center text-sm text-[#64748b]">
                          No twin chats for the current twin selection.
                        </div>
                      ) : (
                        twinChatsForSelectedTwins.map((chat: DemoSourceTwinChat) => (
                          <SelectionCard
                            key={chat.id}
                            title={chat.name}
                            subtitle={`${chat.sourceTwinName} with ${chat.otherTwinName}`}
                            meta={`${chat.sourceUserName} • ${chat.messageCount} messages • ${chat.questionCount} pending answers • ${chat.matchPercentage}% match`}
                            badge="Twin Chat"
                            selected={selectedTwinChatIds.includes(chat.id)}
                            onToggle={() => toggleSelection(chat.id, selectedTwinChatIds, setSelectedTwinChatIds)}
                          />
                        ))
                      )}
                    </div>
                  </div>

                  <div>
                    <div className="mb-3 flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <MessageSquareText className="h-4 w-4 text-[#8fb4ff]" />
                        <h4 className="text-sm font-semibold uppercase tracking-[0.18em] text-[#94a3b8]">Human Chats</h4>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          setSelectedChatIds(
                            selectedChatIds.length === chatsForSelectedTwins.length ? [] : chatsForSelectedTwins.map((chat) => chat.id)
                          )
                        }
                      >
                        {selectedChatIds.length === chatsForSelectedTwins.length ? 'Clear' : 'Select all'}
                      </Button>
                    </div>
                    <div className="grid gap-3">
                      {chatsForSelectedTwins.length === 0 ? (
                        <div className="rounded-2xl border border-dashed border-[#2c3348] bg-[#0e1119] px-4 py-8 text-center text-sm text-[#64748b]">
                          No chats for the current twin selection.
                        </div>
                      ) : (
                        chatsForSelectedTwins.map((chat: DemoSourceChat) => (
                          <SelectionCard
                            key={chat.id}
                            title={chat.name || chat.otherUserName}
                            subtitle={`${chat.sourceTwinName} -> ${chat.otherUserName}`}
                            meta={`${chat.sourceUserName} • ${chat.messageCount} messages • ${chat.matchPercentage}% match`}
                            badge="Human Chat"
                            selected={selectedChatIds.includes(chat.id)}
                            onToggle={() => toggleSelection(chat.id, selectedChatIds, setSelectedChatIds)}
                          />
                        ))
                      )}
                    </div>
                  </div>
                </div>
              )}
            </Card>

            <Card className="border-[#29314a] bg-[#10141f]">
              <CardHeader title="Created demo users" subtitle="Delete a demo user here and the backend removes the user plus all generated data." />
              {loadingDemoUsers ? (
                <div className="flex items-center justify-center py-10">
                  <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#3b82f6] border-t-transparent" />
                </div>
              ) : demoUsers.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-[#2d3650] bg-[#0d111b] px-4 py-10 text-center text-sm text-[#64748b]">
                  No demo users have been created yet.
                </div>
              ) : (
                <div className="space-y-3">
                  {demoUsers.map((user) => (
                    <div key={user.id} className="flex flex-col gap-4 rounded-2xl border border-[#252535] bg-[#0e1119] p-4 lg:flex-row lg:items-center lg:justify-between">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="truncate text-sm font-semibold text-[#f8fafc]">{user.name}</p>
                          <span className="rounded-full bg-[#1b2440] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#8fb4ff]">
                            {userTypeLabel(user.userType)}
                          </span>
                        </div>
                        <p className="mt-1 truncate text-sm text-[#94a3b8]">{user.email}</p>
                        <p className="mt-2 text-xs text-[#64748b]">
                          {user.twinCount} twins • {user.twinChatCount} twin chats • {user.chatCount} chats • created {formatDate(user.createdAt)}
                        </p>
                        {user.sourceUserName && <p className="mt-1 text-xs text-[#64748b]">Sources: {user.sourceUserName}</p>}
                      </div>
                      <div className="flex items-center gap-2">
                        <Link to={`/users/${user.id}`}>
                          <Button variant="ghost" size="sm" icon={<ExternalLink className="h-4 w-4" />}>
                            Open
                          </Button>
                        </Link>
                        <Button
                          variant="danger"
                          size="sm"
                          icon={<Trash2 className="h-4 w-4" />}
                          onClick={() => setDeleteTarget(user)}
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        </div>
      </div>

      <Modal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Delete Demo User"
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button variant="danger" loading={deleting} onClick={handleDeleteDemoUser}>
              Delete Everything
            </Button>
          </>
        }
      >
        <p className="text-sm text-[#94a3b8]">
          This will remove <span className="font-semibold text-[#f8fafc]">{deleteTarget?.name}</span>, the demo user account,
          all generated twins, twin chats, chats, and every other seeded record tracked for that user.
        </p>
      </Modal>
    </Layout>
  );
}
